import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import SelectBox from "components/atoms/SelectBox";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button/Button";
import GeneralText from "components/atoms/text/GeneralText";
import TitleText from "components/atoms/text/TitleText";
import useToast from "components/atoms/toast/useToast";

import RightSideBar from "components/molecules/RightSideBar";
import ResearchDetailTitleInput from "components/organisms/research/ResearchDetailTitleInput";
import ResearchEditor from "components/organisms/research/ResearchEditor";
import AbstractModal from "components/page/modal/AbstractModal";
import ResearchAnalysisModal from "components/page/modal/ResearchAnalysisModal";

import { useBibliographies } from "hooks/queries/useBibliographies";
import { useSelectBoxOptions } from "hooks/queries/useOptions";
import {
    useReferenceCitationCreate,
    useReferenceCitationDeleteByUuid,
    useResearch,
    useResearchDelete,
    useResearchUpdate,
} from "hooks/queries/useResearch";
import { useUser } from "hooks/queries/useUser";

import { permissionErrorHandler } from "utils/request/common";

const SAVE_CHANGE_TIME_INTERVAL = 60 * 1000;
const AUTO_SAVE_INTERVAL = 60 * 1000 * 30;

const WidthLimit = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 auto;
    width: 100%;
    padding: 0 16px;
`;

const Header = styled.div`
    position: fixed;
    top: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
    background-color: var(--color-White);
    border-bottom: solid 1px var(--color-Line);
    z-index: 1000;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin: 48px 0 0 0;
    width: 100%;
`;

const ContentsWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

const CategoryItem = styled.div`
    position: relative;
    display: flex;
    flex-direction: ${(props) => props.direction || "column"};
    justify-content: flex-start;
    align-items: flex-start;
    margin: ${(props) => props.margin || "0 0 24px 0"};
    width: 100%;
    height: 100%;
`;

const ResearchExplainWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    margin: 0;
    width: 100%;
`;

const MainHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px 8px 16px;
    width: 100%;
    border-bottom: 1px solid var(--color-Line);
    flex-shrink: 0;
`;

const SelectBoxSection = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
`;

const ItemWrapper = styled.div`
    flex: 1;
    overflow-y: scroll;
    padding-bottom: 80px;
`;

const RightSidebarFooter = styled.div`
    display: flex;
    justify-content: end;
    align-items: center;
    padding: 16px;
    background-color: var(--color-White);
    border-top: 1px solid var(--color-Line);
    position: absolute;
    bottom: 0;
    width: 100%;
    flex-shrink: 0;
`;

const BackWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 16px 0 0;
    cursor: pointer;
`;

const HeaderLeftSection = styled.div`
    display: flex;
    align-items: center;
`;

const ReferenceSection = styled.div`
    width: 100%;
    min-height: 300px;
    display: flex;
    justify-content: center; /* 가운데 정렬을 위해 추가 */
    padding: 20px 0;
    background-color: #f5f5f5;
`;

const ReferenceWrapper = styled.div`
    width: 70%;
    max-width: 800px;
    background-color: var(--color-White);
    border: 1px solid var(--color-Line);
`;

const ReferenceListTitle = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid var(--color-Line);
`;

const ReferenceList = styled.div`
    padding: 8px 16px;
`;

const ReferenceListItem = styled.div`
    padding: 8px 10px;
    border-bottom: 1px solid var(--color-Line);
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }

    &:hover .reference-button-section {
        display: flex;
    }
`;

const BlankView = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
`;

const BulletWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
`;

const ReferenceRow = styled.div`
    display: flex;
    padding: 8px 10px 8px 10px;
    border-bottom: 1px dashed var(--color-Line);
    justify-content: space-between;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }

    &:hover .reference-button-section {
        display: flex;
    }
`;

const ReferenceTextWrap = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    margin: 0 16px;
    flex-direction: column;
    cursor: default;
`;

const ReferenceButtonWrap = styled.div`
    display: none;
    width: 54px;
    height: 28px;
    background: #ffffff;
    box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.12);
    border-radius: 6px;

    position: absolute;
    left: 100%;
    transform: translateX(calc(-100% - 8px)) translateY(calc(-50% - 8px));

    justify-content: center;
    align-items: center;
`;

const ReferenceListButton = styled.div`
    position: fixed;
    top: 110px;

    display: flex;
    justify-content: center;
    align-items: center;

    padding: 0 12px;
    width: 40px;
    height: 40px;
    background-color: var(--color-Button1);
    box-shadow: 0 0 2px 1px rgba(0, 0, 0, 0.24);
    border-radius: 20px;
    transition: all 240ms ease-in-out;
    cursor: pointer;
    z-index: 10;

    & > .reference-list-button-text {
        opacity: 0;
        transition: all 400ms ease-in-out;
        transition-delay: 80ms;
    }

    &:hover {
        width: 124px;
        background-color: var(--color-ButtonHover1);

        & > .reference-list-button-text {
            opacity: 1;
        }
    }
`;

const RefIconWrap = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
`;

const CitationCountBadge = styled.div`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    flex-direction: row;
    padding: 4px 6px;
    margin: 8px 0 0 0;
    height: 20px;
    border: 1px solid #4d8884;
    border-radius: 4px;
    background-color: var(--color-White);
    color: #4d8884;
    gap: 4px;
`;

function ResearchDetailPage(props) {
    const { uuid } = useParams();

    const uuidRef = useRef(null);
    const navigate = useNavigate();

    const [user, setUser] = useState({});
    const userQuery = useUser();

    const researchInitialState = {
        id: "",
        display_name: "",
        created_at: "",
        updated_at: "",
        cited_references: [],
        html_content: "",
    };
    const [research, setResearch] = useState();
    const [showResearchAnalysisModal, setShowResearchAnalysisModal] = useState(false);
    const researchQuery = useResearch({ uuid: uuid });
    const deleteResearchMutation = useResearchDelete();
    const updateResearchMutation = useResearchUpdate();

    const [showReferenceSidebar, setShowReferenceSidebar] = useState(false);
    const [referenceFilter, setReferenceFilter] = useState("cited");
    const [referenceOrder, setReferenceOrder] = useState("-created_at");
    const [references, setReference] = useState([]);
    const referencesQuery = useBibliographies({
        order: referenceOrder,
        citation_style: research?.citation_style,
    });

    const referenceCitationMutation = useReferenceCitationCreate();
    const referenceCitationDeleteMutation = useReferenceCitationDeleteByUuid();

    const optionsQuery = useSelectBoxOptions();
    const [styleList, setStyleList] = useState([]);
    const [showContextMenu, setShowContextMenu] = useState({ show: false, x: 0, y: 0 });

    const [lastHtmlContentChangeTime, setLastHtmlContentChangeTime] = useState(new Date());
    const { setToast } = useToast();
    const editorRef = useRef();

    useEffect(() => {
        if (optionsQuery.data) {
            setStyleList(optionsQuery.data.env_citation_style);
        }
    }, [optionsQuery.data]);

    // 초기화
    useEffect(() => {
        return () => {
            setResearch(researchInitialState);
            researchQuery.remove();
        };
    }, []);

    useEffect(() => {
        if (uuid !== uuidRef.current) {
            setResearch(researchInitialState);
            researchQuery.refetch();
            uuidRef.current = uuid;
        }
    }, [uuid]);

    useEffect(() => {
        if (researchQuery.data) {
            setResearch((prev) => ({
                ...prev,
                ...researchQuery.data,
            }));
        }

        if (researchQuery.error) {
            permissionErrorHandler(researchQuery.error, navigate);
        }

        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [researchQuery.data, researchQuery.error, userQuery.data]);

    useEffect(() => {
        if (referencesQuery.data) {
            setReference(referencesQuery.data);
        }
    }, [referencesQuery.data]);

    // 리서치 삭제
    const handleDeleteResearch = () => {
        Confirm("warn", "리서치 삭제", "리서치를 삭제하시겠습니까?", "삭제", (e) => {
            deleteResearchMutation.mutate({ id: research.id });
            navigate("/reference");
            setToast("성공적으로 삭제되었습니다.", "info");
        });
    };

    const updateResearchEditor = () => {
        updateResearchMutation.mutate(
            {
                id: research.id,
                html_content: research?.html_content,
            },
            {
                onSuccess: (data, variables, context) => {
                    setToast("저장되었습니다.", "info");
                },
                onError: (error) => {
                    setToast("저장에 실패했습니다.", "error");
                },
            },
        );
    };

    const autoSaveEditor = () => {
        if (researchQuery.data?.html_content !== research?.html_content) {
            updateResearchMutation.mutate(
                {
                    id: research.id,
                    html_content: research?.html_content,
                },
                {
                    onSuccess: (data, variables, context) => {
                        setToast("자동 저장되었습니다.", "info");
                    },
                },
            );
        }
    };

    useEffect(() => {
        const intervalId = setInterval(() => {
            autoSaveEditor();
        }, SAVE_CHANGE_TIME_INTERVAL); // 60초마다 체크

        return () => clearInterval(intervalId);
    }, [lastHtmlContentChangeTime, autoSaveEditor]);

    useEffect(() => {
        const autoSaveIntervalId = setInterval(() => {
            if (Date.now() - new Date(research?.updated_at).getTime() > AUTO_SAVE_INTERVAL) {
                // 30분마다 저장
                autoSaveEditor();
            }
        }, AUTO_SAVE_INTERVAL); // 30분마다 체크

        return () => clearInterval(autoSaveIntervalId);
    }, [research?.updated_at, autoSaveEditor]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === "s") {
                event.preventDefault();
                updateResearchEditor();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [research?.html_content]);

    const citationButtonRef = useRef(null);

    useEffect(() => {
        const updateCitationButtonPosition = () => {
            if (editorRef.current) {
                const quillRight = editorRef.current.getEditor().root.getBoundingClientRect().right;
                citationButtonRef.current.style.left = `${quillRight + 16}px`;
            }
        };

        const checkEditorRef = () => {
            if (editorRef.current) {
                updateCitationButtonPosition();
            } else {
                setTimeout(checkEditorRef, 100); // 100ms 후에 다시 시도
            }
        };

        // 초기 위치 설정
        checkEditorRef();

        // 윈도우 리사이즈 이벤트에 대응하여 위치 업데이트
        window.addEventListener("resize", updateCitationButtonPosition);

        // 클린업 함수로 이벤트 리스너 제거
        return () => {
            window.removeEventListener("resize", updateCitationButtonPosition);
        };
    }, [editorRef.current]);

    return (
        <>
            <HtmlHead title={research?.display_name} />
            <Header>
                <WidthLimit>
                    <BackWrap onClick={() => navigate(-1)}>
                        <Button
                            width={"66px"}
                            height={"32px"}
                            bgColor={"var(--color-Button4)"}
                            hoverBgColor={"var(--color-ButtonHover4)"}
                            fontColor={"var(--color-Black)"}
                            buttonText="뒤로"
                        >
                            <Icon name="arrowLeft" size="12px" />
                        </Button>
                    </BackWrap>

                    <ResearchDetailTitleInput
                        name={"display_name"}
                        placeholder={"리서치 표시 이름을 입력해주세요."}
                        value={research?.display_name}
                        onChange={(e) => {
                            setResearch({ ...research, display_name: e.target.value });
                        }}
                        allowEmpty={false}
                        saveAct={(e) => {
                            updateResearchMutation.mutate({
                                id: research.id,
                                display_name: research?.display_name,
                            });
                        }}
                        fontsize={"16px"}
                        fontweight={"500"}
                        padding={"2px"}
                    />
                    <Button
                        margin={"0 0 0 16px"}
                        width={"66px"}
                        height={"32px"}
                        bgColor={"var(--color-Button4)"}
                        hoverBgColor={"var(--color-ButtonHover4)"}
                        buttonText={"삭제"}
                        fontColor={"var(--color-Black)"}
                        onClick={handleDeleteResearch}
                    >
                        <Icon name="delete" size="12px" />
                    </Button>
                </WidthLimit>
            </Header>

            <Body>
                <ContentsWrapper>
                    <CategoryItem direction="column" margin="0">
                        <ResearchExplainWrap>
                            {research?.uuid && (
                                <ResearchEditor
                                    value={research?.html_content}
                                    onChange={(e) => {
                                        if (e === research.html_content) return;
                                        setLastHtmlContentChangeTime(Date.now());
                                        setResearch((prev) => ({ ...prev, html_content: e }));
                                    }}
                                    setShowReferenceSidebar={setShowReferenceSidebar}
                                    research={research}
                                    updateResearchMutation={updateResearchMutation}
                                    referenceCitationMutation={referenceCitationMutation}
                                    ref={editorRef}
                                />
                            )}
                        </ResearchExplainWrap>
                    </CategoryItem>
                </ContentsWrapper>
                <ReferenceSection>
                    <ReferenceWrapper>
                        <ReferenceListTitle>
                            <TitleText size={"regular"} color={"var(--color-Black)"}>
                                참고문헌
                            </TitleText>
                            <div style={{ display: "flex" }}>
                                <SelectBox
                                    value={research?.citation_style}
                                    items={styleList}
                                    onChange={(value) => {
                                        updateResearchMutation.mutate({
                                            id: research.id,
                                            html_content: research?.html_content,
                                            citation_style: value,
                                        });
                                    }}
                                    width={"120px"}
                                />
                            </div>
                        </ReferenceListTitle>
                        <ReferenceList>
                            {research?.cited_references?.length !== 0 ? (
                                research?.cited_references?.map((reference, index) => (
                                    <ReferenceListItem key={index}>
                                        <GeneralText
                                            size={"small"}
                                            style={{ cursor: "default" }}
                                            color={"var(--color-Black)"}
                                        >
                                            {reference.styled_text}
                                        </GeneralText>
                                        <ReferenceButtonWrap className={"reference-button-section"}>
                                            <Tooltip message={"인용해제"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"transparent"}
                                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                                    onClick={() => {
                                                        referenceCitationDeleteMutation.mutate({
                                                            research_id: research.id,
                                                            reference_id: reference.id,
                                                            research_uuid: research.uuid,
                                                            html_content: editorRef.current.getEditor().root.innerHTML,
                                                        });
                                                    }}
                                                >
                                                    <Icon
                                                        name={"quoteRemove"}
                                                        color={"var(--color-Black)"}
                                                        size={"12px"}
                                                    />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip message={"상세보기"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"transparent"}
                                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                                    onClick={() => {
                                                        window.open(`/reference/${reference.uuid}`, "_blank");
                                                    }}
                                                >
                                                    <Icon
                                                        name={"newTabOpen"}
                                                        size={"12px"}
                                                        color={"var(--color-Black)"}
                                                    />
                                                </Button>
                                            </Tooltip>
                                        </ReferenceButtonWrap>
                                    </ReferenceListItem>
                                ))
                            ) : (
                                <BlankView>
                                    <BulletWrap>
                                        <Icon name={"bullet"} color={"var(--color-Grey2)"} />
                                    </BulletWrap>
                                    <GeneralText size={"large"} color={"var(--color-Grey2)"}>
                                        인용된 참고문헌이 없습니다.
                                    </GeneralText>
                                </BlankView>
                            )}
                        </ReferenceList>
                    </ReferenceWrapper>
                </ReferenceSection>
            </Body>
            <RightSideBar right={showReferenceSidebar ? "0" : "-560px"}>
                <MainHeader>
                    <HeaderLeftSection>
                        <Button
                            onlyIcon
                            bgColor={"transparent"}
                            hoverBgColor={"var(--color-ButtonHover4)"}
                            onClick={() => setShowReferenceSidebar(false)}
                        >
                            <Icon name={"x"} />
                        </Button>
                        <GeneralText size={"large"} color={"var(--color-Black)"} margin={"0 0 0 8px"}>
                            참고문헌 관리
                        </GeneralText>
                    </HeaderLeftSection>
                    <SelectBoxSection>
                        <SelectBox
                            width="calc(100% - 28px)"
                            maxWidth="120px"
                            optionWidth="100%"
                            optionMinWidth="120px"
                            topBottom="top"
                            reverse="180deg"
                            margin={"0 4px 0 0"}
                            value={referenceFilter}
                            items={[
                                { id: "all", name: "모든 참고문헌" },
                                { id: "cited", name: "연결된 참고문헌" },
                            ]}
                            onChange={(value) => setReferenceFilter(value)}
                        />
                        <SelectBox
                            width="120px"
                            maxWidth="120px"
                            optionWidth="100%"
                            optionMinWidth="120px"
                            topBottom="top"
                            reverse="180deg"
                            value={referenceOrder}
                            items={[
                                { id: "-created_at", name: "최근 추가순" },
                                { id: "-updated_at", name: "최근 활동순" },
                                { id: "display_name", name: "제목 순" },
                            ]}
                            onChange={(value) => setReferenceOrder(value)}
                        />
                    </SelectBoxSection>
                </MainHeader>
                <ItemWrapper>
                    {references.length !== 0 ? (
                        referenceFilter === "all" ? (
                            references.map((reference, index) => (
                                <ReferenceRow key={index}>
                                    <ReferenceTextWrap>
                                        <GeneralText size={"small"}>{reference.styled_text}</GeneralText>
                                        {research.cited_references?.find((cited) => cited.id === reference.id)
                                            ?.citation_count > 0 && (
                                            <CitationCountBadge>
                                                <Icon name={"check"} size={"10px"} color={"#00725d"} />
                                                <GeneralText size={"small"} color={"#4D8884"}>
                                                    {
                                                        research.cited_references?.find(
                                                            (cited) => cited.id === reference.id,
                                                        )?.citation_count
                                                    }
                                                    회 인용
                                                </GeneralText>
                                            </CitationCountBadge>
                                        )}
                                    </ReferenceTextWrap>
                                    <ReferenceButtonWrap className={"reference-button-section"}>
                                        <Tooltip message={"인용하기"}>
                                            <Button
                                                onlyIcon
                                                bgColor={"transparent"}
                                                hoverBgColor={"var(--color-ButtonHover4)"}
                                                onClick={() => {
                                                    editorRef.current.addReference(reference.uuid);
                                                }}
                                            >
                                                <Icon name={"quoteAdd"} color={"var(--color-Black)"} />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip message={"상세보기"}>
                                            <Button
                                                onlyIcon
                                                bgColor={"transparent"}
                                                hoverBgColor={"var(--color-ButtonHover4)"}
                                                onClick={() => {
                                                    window.open(`/reference/${reference.uuid}`, "_blank");
                                                }}
                                            >
                                                <Icon name={"newTabOpen"} size={"12px"} color={"var(--color-Black)"} />
                                            </Button>
                                        </Tooltip>
                                    </ReferenceButtonWrap>
                                </ReferenceRow>
                            ))
                        ) : (
                            research?.references?.map((reference, index) => (
                                <ReferenceRow key={index}>
                                    <ReferenceTextWrap>
                                        <GeneralText size={"small"}>{reference.styled_text}</GeneralText>
                                        {research.cited_references?.find((cited) => cited.id === reference.id)
                                            ?.citation_count > 0 && (
                                            <CitationCountBadge>
                                                <Icon name={"check"} size={"10px"} color={"#00725d"} />
                                                <GeneralText size={"small"} color={"#4D8884"}>
                                                    {
                                                        research.cited_references?.find(
                                                            (cited) => cited.id === reference.id,
                                                        )?.citation_count
                                                    }
                                                    회 인용
                                                </GeneralText>
                                            </CitationCountBadge>
                                        )}
                                    </ReferenceTextWrap>
                                    <ReferenceButtonWrap className={"reference-button-section"}>
                                        <Tooltip message={"인용하기"}>
                                            <Button
                                                onlyIcon
                                                bgColor={"transparent"}
                                                hoverBgColor={"var(--color-ButtonHover4)"}
                                                onClick={() => {
                                                    editorRef.current.addReference(reference.uuid);
                                                }}
                                            >
                                                <Icon name={"quoteAdd"} size={"12px"} color={"var(--color-Black)"} />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip message={"상세보기"}>
                                            <Button
                                                onlyIcon
                                                bgColor={"transparent"}
                                                hoverBgColor={"var(--color-ButtonHover4)"}
                                                onClick={() => {
                                                    window.open(`/reference/${reference.uuid}`, "_blank");
                                                }}
                                            >
                                                <Icon name={"newTabOpen"} color={"var(--color-Black)"} />
                                            </Button>
                                        </Tooltip>
                                    </ReferenceButtonWrap>
                                </ReferenceRow>
                            ))
                        )
                    ) : (
                        <BlankView>
                            <BulletWrap>
                                <Icon name={"bullet"} color={"var(--color-Grey2)"} />
                            </BulletWrap>
                            <GeneralText size={"large"} color={"var(--color-Grey2)"}>
                                연결된 참고문헌이 없습니다.
                            </GeneralText>
                        </BlankView>
                    )}
                </ItemWrapper>
                <RightSidebarFooter>
                    <Button
                        height={"32px"}
                        width={"120px"}
                        bgColor={"var(--color-Button2)"}
                        hoverBgColor={"var(--color-ButtonHover2)"}
                        buttonText={"자동분석"}
                        onClick={() => setShowResearchAnalysisModal(true)}
                    >
                        <Icon name={"aiResponse"} color={"var(--color-White)"} />
                    </Button>
                </RightSidebarFooter>
            </RightSideBar>
            {showResearchAnalysisModal && (
                <AbstractModal
                    modalTitle="참고문헌 자동분석"
                    width={1060}
                    height={820}
                    exitModal={(e) => setShowResearchAnalysisModal(!showResearchAnalysisModal)}
                >
                    <ResearchAnalysisModal research={research} user={user} />
                </AbstractModal>
            )}
            <ReferenceListButton onClick={() => setShowReferenceSidebar(true)} ref={citationButtonRef}>
                <GeneralText
                    ellipsis
                    margin={"0 20px 0 0"}
                    size={"small"}
                    color={"var(--color-White)"}
                    className={"reference-list-button-text"}
                >
                    참고문헌 목록
                </GeneralText>
                <RefIconWrap>
                    <Icon name={"quote"} size={"16px"} color={"var(--color-White)"} />
                </RefIconWrap>
            </ReferenceListButton>
        </>
    );
}

export default ResearchDetailPage;
